import React, { useEffect, useRef, useState } from 'react';
import { CollapsibleSection, EventSectionsSkeleton } from './ReadOnlyEventSection';
import ReadOnlyEventOverview, { formatOccurrenceDateTime } from './ReadOnlyEventOverview';
import {
  buildDefaultOpenSections,
  isRecurringRsvpEvent,
  rsvpNeedsResponse as getRsvpNeedsResponse,
} from './readOnlyEventPriority';
import EventRegister from './EventRegister';
import AttendeesSection from './sections/attendees';
import PublicEventShareActions from './PublicEventShareActions';

export default function ReadOnlyEventView({
  form,
  isExisting,
  isAdmin,
  canViewAttendees = false,
  attendeesReadOnly = false,
  onEdit,
  onEditOccurrence,
  onEditSeries,
  onCopy,
  onClose,
  modalStyle,
  rolesBlock,   // optional node (event roles + requests)
  rsvpBlock,    // optional node (RSVP controls)
  ticketBlock,  // optional node (Ticket controls)
  outingBlock,  // optional node (linked outing status)
  commentsBlock, // optional node (event comments)
  extraRsvpRows = [],
  focusComments = false,
  commentsAutoOpen = false,
  outingAutoOpen = false,
  rolesAutoOpen = false,
  rolesUnfilledCount = 0,
  resolvingPriority = false,
  myRsvpStatus = null,
  myRsvpOccurrenceStatus = null,
  myRsvpSeriesStatus = null,
  rsvpSeriesResponsesEnabled = true,
  calendarSurface = 'unknown',
  canManageEventRefunds = false,
  db,
  teamId,
}) {
  const hasRsvpBlock = Boolean(rsvpBlock);
  const hasTicketBlock = Boolean(ticketBlock);
  const hasCommentsBlock = Boolean(commentsBlock);
  const isRecurringRsvp = isRecurringRsvpEvent(form);
  const needsRsvpResponse = getRsvpNeedsResponse({
    hasRsvpBlock,
    isRecurringRsvp,
    myRsvpStatus,
    myRsvpOccurrenceStatus,
    myRsvpSeriesStatus,
    rsvpSeriesResponsesEnabled,
  });

  const [showAttendees, setShowAttendees] = useState(false);
  const [showEditScopeMenu, setShowEditScopeMenu] = useState(false);
  const [initialSettling, setInitialSettling] = useState(true);
  const [openSections, setOpenSections] = useState(() => buildDefaultOpenSections({
    hasRsvpBlock,
    hasTicketBlock,
    hasCommentsBlock,
    rsvpNeedsResponse: needsRsvpResponse,
    rolesAutoOpen,
    outingAutoOpen,
    commentsAutoOpen,
    focusComments,
  }));
  const commentsRef = useRef(null);
  const userChangedSectionsRef = useRef(false);

  const canShowAttendeesButton =
    isExisting &&
    canViewAttendees &&
    (form.attendanceMode === 'rsvp' || form.attendanceMode === 'ticketed');

  const canRenderAttendeesSection =
    canShowAttendeesButton && showAttendees && form.id && teamId && db;

  const showingAttendees = !!canRenderAttendeesSection;
  const showSectionSkeleton =
    !showingAttendees &&
    !userChangedSectionsRef.current &&
    (initialSettling || resolvingPriority);
  const showScopedEditMenu =
    isExisting &&
    isAdmin &&
    !showingAttendees &&
    typeof onEditOccurrence === 'function' &&
    typeof onEditSeries === 'function';
  const occurrenceDateTime = formatOccurrenceDateTime(form);

  useEffect(() => {
    userChangedSectionsRef.current = false;
    setInitialSettling(true);
    const timer = window.setTimeout(() => {
      setInitialSettling(false);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [form.id, form.occurrenceId, form.originalEventId]);

  useEffect(() => {
    if (userChangedSectionsRef.current) return;
    setOpenSections(buildDefaultOpenSections({
      hasRsvpBlock,
      hasTicketBlock,
      hasCommentsBlock,
      rsvpNeedsResponse: needsRsvpResponse,
      rolesAutoOpen,
      outingAutoOpen,
      commentsAutoOpen,
      focusComments,
    }));
  }, [
    commentsAutoOpen,
    focusComments,
    hasCommentsBlock,
    hasRsvpBlock,
    hasTicketBlock,
    outingAutoOpen,
    rolesAutoOpen,
    rolesUnfilledCount,
    needsRsvpResponse,
  ]);

  useEffect(() => {
    if (showingAttendees) setShowEditScopeMenu(false);
  }, [showingAttendees]);

  useEffect(() => {
    if (!focusComments || showSectionSkeleton || showingAttendees || !openSections.comments || !commentsRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => {
      commentsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusComments, openSections.comments, showSectionSkeleton, showingAttendees]);

  const toggleSection = (section) => {
    userChangedSectionsRef.current = true;
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content event-modal event-readonly-modal"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'start',
            gap: 12,
          }}
        >
          <div className="event-readonly-header__title">
            <h2 className="modal-title">{form.name || 'Event'}</h2>
            {occurrenceDateTime ? (
              <div className="event-readonly-header__occurrence">
                This occurrence: {occurrenceDateTime}
              </div>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {hasTicketBlock && form._hasAdmissionBooking === true && form.admissionTicketsEnabled === true && <a href={`/tickets/${teamId}/${form.id}`} className="btn btn--primary btn--sm">{form._hasPaidAdmission === true ? 'My tickets' : 'View admission status'}</a>}
            {(isAdmin || form._canCheckIn === true) && form.attendanceMode === 'ticketed' && form.admissionTicketsEnabled === true && <a href={`/teams/${teamId}/events/${form.id}/check-in${form.occurrenceId ? `?occurrence=${encodeURIComponent(form.occurrenceId)}` : ''}`} className="btn btn--secondary btn--sm">Check in</a>}
            {canShowAttendeesButton && (
              <button
                type="button"
                onClick={() => setShowAttendees((v) => !v)}
                className="btn btn--secondary btn--sm"
              >
                {showAttendees ? 'Hide attendees' : 'Attendees'}
              </button>
            )}
            {/* Hide Edit while viewing attendees */}
            {showScopedEditMenu && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowEditScopeMenu((open) => !open)}
                  className="btn btn--secondary btn--sm"
                >
                  Edit
                </button>
                {showEditScopeMenu && (
                  <div
                    className="card"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 'calc(100% + 6px)',
                      minWidth: 210,
                      zIndex: 20,
                      padding: 6,
                    }}
                  >
                    <div className="text-muted" style={{ fontSize: 12, padding: '2px 4px' }}>
                      Single occurrence
                    </div>
                    <button
                      type="button"
                      className="button button--xs"
                      style={{ width: '100%', justifyContent: 'flex-start' }}
                      onClick={() => {
                        setShowEditScopeMenu(false);
                        onEditOccurrence?.();
                      }}
                    >
                      Edit this occurrence
                    </button>
                    <div
                      className="text-muted"
                      style={{ fontSize: 12, padding: '6px 4px 2px', borderTop: '1px solid #e5e7eb', marginTop: 6 }}
                    >
                      Entire series
                    </div>
                    <button
                      type="button"
                      className="button button--xs"
                      style={{ width: '100%', justifyContent: 'flex-start', marginTop: 6 }}
                      onClick={() => {
                        setShowEditScopeMenu(false);
                        onEditSeries?.();
                      }}
                    >
                      Edit series
                    </button>
                  </div>
                )}
              </div>
            )}
            {!showScopedEditMenu && isExisting && isAdmin && !showingAttendees && (
              <button
                type="button"
                onClick={onEdit}
                className="btn btn--secondary btn--sm"
              >
                Edit
              </button>
            )}
            {isExisting && isAdmin && !showingAttendees && typeof onCopy === 'function' && (
              <button
                type="button"
                onClick={() => onCopy(form)}
                className="btn btn--secondary btn--sm"
              >
                Copy event
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn--ghost btn--sm"
            >
              Close
            </button>
          </div>
        </div>

        {isExisting && isAdmin && <EventRegister key={`${form.id}:${form.occurrenceId || ''}`} teamId={teamId} form={form} />}

        {/* Hide event summary + ticket/RSVP blocks when showing attendees */}
        {!showingAttendees && (
          <div
            style={{
              display: 'grid',
              gap: 10,
            }}
          >
            <PublicEventShareActions form={form} isAdmin={isAdmin} justCreated={!isExisting} />
            <ReadOnlyEventOverview form={form} />

            {showSectionSkeleton ? (
              <EventSectionsSkeleton />
            ) : (
              <>
                {/* Prefer ticket UI when present; otherwise RSVP if provided */}
                {ticketBlock ? (
                  <CollapsibleSection
                    id="tickets"
                    title="Tickets"
                    summary="Booking and checkout"
                    open={openSections.tickets}
                    onToggle={() => toggleSection('tickets')}
                  >
                    {ticketBlock}
                  </CollapsibleSection>
                ) : (
                  <CollapsibleSection
                    id="rsvp"
                    title="Your RSVP"
                    summary={isRecurringRsvp ? (rsvpSeriesResponsesEnabled === false ? 'This date' : 'This date and series') : 'Response'}
                    open={openSections.rsvp}
                    onToggle={() => toggleSection('rsvp')}
                  >
                    {rsvpBlock}
                  </CollapsibleSection>
                )}

                <CollapsibleSection
                  id="roles"
                  title="Event roles"
                  summary={rolesUnfilledCount > 0 ? 'Needs coverage' : 'Volunteers and assignments'}
                  badgeCount={rolesUnfilledCount}
                  badgeLabel={`${rolesUnfilledCount} unfulfilled role slot${rolesUnfilledCount === 1 ? '' : 's'}`}
                  open={openSections.roles}
                  onToggle={() => toggleSection('roles')}
                >
                  {rolesBlock}
                </CollapsibleSection>

                <CollapsibleSection
                  id="outing"
                  title="Outing"
                  summary="Crew and allocation status"
                  open={openSections.outing}
                  onToggle={() => toggleSection('outing')}
                >
                  {outingBlock}
                </CollapsibleSection>

                <CollapsibleSection
                  id="comments"
                  title="Comments"
                  summary="Event discussion"
                  open={openSections.comments}
                  onToggle={() => toggleSection('comments')}
                  sectionRef={commentsRef}
                >
                  {commentsBlock}
                </CollapsibleSection>
              </>
            )}
          </div>
        )}

        {showingAttendees && (
          <AttendeesSection
            db={db}
            teamId={teamId}
            eventId={form.id}
            attendanceMode={form.attendanceMode}
            occurrenceId={form.occurrenceId || null}
            isRecurring={isRecurringRsvp}
            readOnly={attendeesReadOnly}
            extraRsvpRows={extraRsvpRows}
            rsvpSeriesResponsesEnabled={rsvpSeriesResponsesEnabled}
            calendarSurface={calendarSurface}
            ticketPaymentBasis={form.ticketPaymentBasis || ''}
            canManageRefunds={canManageEventRefunds}
          />
        )}
      </div>
    </div>
  );
}
